/* eslint-disable react-hooks/exhaustive-deps */
import { useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Box,
  LinearProgress,
  ListItem,
  makeStyles,
  Tooltip,
  Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Details from "components/EmailEventDetails";
import apiRequest from "utils/apiRequestWithErrorCode";

import { useParams } from "react-router-dom";
import { EmailFlowLogDetails, RecipientByEmail } from "@dashboard-v3/api";

interface Props {
  details: RecipientByEmail;
}

export default function RecipientDetails(props: Props) {
  const { t } = useTranslation("emailFlowLogs");
  const { details } = props;
  const { id: parentId } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [flowDetails, setFlowDetails] = useState<EmailFlowLogDetails[]>([]);
  const [expanded, setExpanded] = useState(false);
  const classes = useStyles();
  const latestStatusLabel = t(
    `recipientsDetails.latestStatus.${details.latestAction}`
  );

  const getDate = (timestamp: string | number) => {
    const format = "MMM D, YYYY, h:mm:ss A";
    return dayjs(timestamp).format(format);
  };

  const fetchItemDetails = async () => {
    if (flowDetails.length) return;

    setLoading(true);
    try {
      const { msgUID, receivedId } = details;
      const usesParentMsgUID = msgUID === parentId && receivedId;
      const url = usesParentMsgUID
        ? `/emailflow-logs/details/${msgUID}?receivedId=${receivedId}`
        : `/emailflow-logs/details/${msgUID}`;
      const result = await apiRequest<EmailFlowLogDetails[]>("GET", url);
      setFlowDetails(result);
    } catch (error) {
      enqueueSnackbar(t("error.fetchItemEvents"), {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggle: AccordionProps["onChange"] = (_, expanded) => {
    setExpanded(expanded);

    if (expanded && !loading) fetchItemDetails();
  };

  return (
    <ListItem disableGutters>
      <Accordion
        onChange={toggle}
        expanded={expanded}
        classes={{ root: classes.accordionRoot }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          classes={{
            root: classes.summaryRoot,
            content: classes.summaryContent,
          }}
        >
          <div className={classes.dataContainer}>
            <Tooltip title={details.recipient}>
              <Typography noWrap variant="caption" style={{ fontSize: 14 }}>
                {details.recipient}
              </Typography>
            </Tooltip>
            <Typography
              display="inline"
              variant="caption"
              style={{ marginLeft: 12, fontSize: 14 }}
            >
              {latestStatusLabel}
            </Typography>
            <Typography
              display="inline"
              variant="caption"
              style={{ marginLeft: 25, fontSize: 14 }}
            >
              {getDate(details.actionCreatedAt)}
            </Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          {loading && (
            <Box>
              <LinearProgress />
            </Box>
          )}
          {!loading && !!flowDetails.length && (
            <Details details={flowDetails} />
          )}
        </AccordionDetails>
      </Accordion>
    </ListItem>
  );
}

const useStyles = makeStyles({
  headerContainer: {
    marginTop: 20,
    display: "grid",
    gridColumnGap: "20px",
    gridTemplateColumns: "1fr 1fr 1fr",
    padding: "0 16px 0 16px",
    alignItems: "center",
  },
  headerItem: {
    color: "grey",
    fontSize: "14px",
    fontWeight: 500,
  },
  accordionRoot: {
    width: "100%",
    padding: "8px 5px",
    "&.MuiAccordionSummary-content": {
      margin: 0,
    },
    "&.MuiAccordion-rounded:first-child": {
      borderTopLeftRadius: "8px",
      borderTopRightRadius: "8px",
      borderBottomLeftRadius: "8px",
      borderBottomRightRadius: "8px",
    },
    "& .MuiGrid-item:first-of-type > .MuiBox-root": {
      margin: 0,
    },
  },
  accordionDetails: {
    display: "block",
    paddingBottom: "20px",
    paddingTop: "10px",
    "&.MuiBox-root": {
      marginTop: 0,
    },
  },
  summaryRoot: {
    "&.MuiAccordionSummary-root.Mui-expanded": {
      minHeight: 0,
    },
  },
  summaryContent: {
    "&.MuiAccordionSummary-root.Mui-expanded": {
      minHeight: 0,
    },
    "&.MuiAccordionSummary-content": {
      margin: 0,
    },
  },
  dataContainer: {
    display: "grid",
    width: "100%",
    gridColumnGap: "20px",
    gridTemplateColumns: "1fr 1fr 1fr",
    alignItems: "center",
    padding: 0,
  },
});
